/**
 * Institution accent for Encuentro (Alumnia UI 1.2.1, `InstitutionAccent`).
 *
 * The college colour is identity, not action: `accent` is the raw colour for
 * decorative fills, `accentText` is the same hue walked toward ink in 5 % steps
 * until it reads 4.5:1 on every ground it sits on, and `accentSoft` is a 10 %
 * wash (30 % over the dark surface). Buttons are ink, so no text ever sits on
 * the accent fill.
 *
 * Port of `accentFor` from the artifact's `bundle.src.js`. The only deviation
 * is that colours come back normalised to lowercase `#rrggbb`.
 *
 * The org layout calls `encuentroAccentCss()` on the server and emits the
 * result in a `<style>` only in Encuentro mode. Without a configured colour it
 * returns '' and the generated defaults (`accent` = `brand`) apply.
 */
import { contrastRatio, isHexColor, mix, normalizeHex } from './contrast'
import { encuentroSelector, type EncuentroTheme } from './encuentro-scope'

/** Grounds `accentText` must read on: bg + canvas (light), bg + surface (dark). */
const GROUNDS: Record<EncuentroTheme, readonly [string, string]> = {
  light: ['#ffffff', '#f3f5f8'],
  dark: ['#0e1420', '#161e2e'],
}
/** Where `accentText` walks to: the ink ground of each theme. */
const TOWARD: Record<EncuentroTheme, string> = { light: '#0e1420', dark: '#ffffff' }
const ACCENT_INK: Record<EncuentroTheme, string> = { light: '#ffffff', dark: '#0e1420' }
const MIN_RATIO = 4.5
const STEP = 0.05
const MAX_STEPS = 40

export type AccentResult = {
  accent: string
  accentText: string
  accentSoft: string
  /** Legacy: text on an accent fill. 1.2 puts no text on accent fills. */
  accentInk: string
  /** True when `accentText` had to be corrected. Settings warns the admin. */
  adjusted: boolean
  /** How far `accentText` moved toward ink, in percent. */
  shift: number
  /** Lowest contrast of `accentText` on its grounds, two decimals. */
  ratio: number
}

export function accentFor(hex: string, theme: EncuentroTheme = 'light'): AccentResult {
  const accent = normalizeHex(hex)
  const accentSoft = theme === 'dark' ? mix('#161e2e', accent, 0.3) : mix('#ffffff', accent, 0.1)
  const grounds = GROUNDS[theme]
  // On dark the wash is lighter than the surface (30 % of the colour), so text
  // that clears the two page grounds can still sit under 4.5:1 on it (dark brand
  // blues: 4.3:1 in the wizard preview). On light the wash is darker than the
  // grounds by 10 % at most and the page grounds stay the binding ones.
  const walkGrounds = theme === 'dark' ? [...grounds, accentSoft] : grounds
  const readsOnAll = (c: string) => walkGrounds.every((g) => contrastRatio(c, g) >= MIN_RATIO)

  let accentText = accent
  let steps = 0
  while (!readsOnAll(accentText) && steps < MAX_STEPS) {
    accentText = mix(accentText, TOWARD[theme], STEP)
    steps++
  }

  return {
    accent,
    accentText,
    accentSoft,
    accentInk: ACCENT_INK[theme],
    adjusted: steps > 0,
    shift: steps * 5,
    ratio: Math.round(Math.min(...grounds.map((g) => contrastRatio(accentText, g))) * 100) / 100,
  }
}

/** The `--en-accent*` custom properties for one theme. */
export function accentVars(hex: string, theme: EncuentroTheme = 'light'): Record<string, string> {
  const a = accentFor(hex, theme)
  return {
    '--en-accent': a.accent,
    '--en-accent-text': a.accentText,
    '--en-accent-soft': a.accentSoft,
    '--en-accent-ink': a.accentInk,
  }
}

/**
 * CSS that overrides the default accent with the institution colour, light
 * and dark, under the Encuentro scope. Returns '' for a missing or invalid
 * colour: the value ends up inside a `<style>`, so only `#rgb`/`#rrggbb` pass.
 */
export function encuentroAccentCss(hex: string | null | undefined): string {
  if (!isHexColor(hex)) return ''
  return (['light', 'dark'] as const)
    .map((theme) => {
      const body = Object.entries(accentVars(hex, theme))
        .map(([k, v]) => `${k}:${v}`)
        .join(';')
      return `${encuentroSelector(theme)}{${body}}`
    })
    .join('')
}
